import React from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import BadgeFlussoCommerciale from '../../components/BadgeFlussoCommerciale';
import './GuidaCicloAttivoPage.css';

// Pagina statica di documentazione: nessuna API chiamata.
const API_DOCS = [] as const;

export default function GuidaCicloAttivoPage() {
  return (
    <Layout>
      <div className="bop-guida-ciclo-attivo">
        <header className="bop-guida-ciclo-attivo__header">
          <h1 className="bop-guida-ciclo-attivo__titolo">
            Come funziona il ciclo attivo in BOP
          </h1>
          <p className="bop-guida-ciclo-attivo__sottotitolo">
            Guida per capire quando creare un preventivo VENDITA e quando un
            preventivo APPALTO/CONTRATTO, e cosa cambia a valle.
          </p>
        </header>

        <nav className="bop-guida-ciclo-attivo__indice" aria-label="Indice della guida">
          <h2 className="bop-guida-ciclo-attivo__indice-titolo">Indice</h2>
          <ol className="bop-guida-ciclo-attivo__indice-lista">
            <li><a href="#introduzione">Introduzione</a></li>
            <li><a href="#flusso-vendita">Il flusso Vendita</a></li>
            <li><a href="#flusso-appalto">Il flusso Appalto/Contratto</a></li>
            <li><a href="#come-scegliere">Come scegliere alla creazione</a></li>
            <li><a href="#cosa-cambia-valle">Cosa cambia a valle</a></li>
            <li><a href="#casi-pratici">Casi pratici</a></li>
            <li><a href="#errori-comuni">Errori comuni</a></li>
            <li><a href="#faq">FAQ</a></li>
            <li><a href="#glossario">Glossario</a></li>
            <li><a href="#riferimenti">Riferimenti</a></li>
          </ol>
        </nav>

        {/* Contenuto: vedi prompt 09. Per ora placeholder. */}
        <main className="bop-guida-ciclo-attivo__contenuto">
          <section id="introduzione" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Introduzione</h2>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Ogni preventivo che facciamo in BOP, dopo aver acceso il wizard di creazione,
              chiede una scelta che prima del Nodo 5 lotto L2-pre non esisteva: il
              <strong> flusso commerciale</strong>. È la prima domanda del wizard ed è la
              più importante, perché determina <em>tutto</em> quello che succederà a valle:
              il modo in cui consegniamo, il modo in cui fatturiamo, e in alcuni casi il
              fatto stesso che ci sia o meno un capitolato firmato.
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Esistono solo due flussi possibili: <BadgeFlussoCommerciale flusso="VENDITA" />
              {' '}e <BadgeFlussoCommerciale flusso="APPALTO_CONTRATTO" />. La scelta è
              obbligatoria, ed è <strong>definitiva</strong>: una volta creato il
              preventivo, il flusso non si cambia più. Se sbagli, non c'è un bottone
              "cambia flusso": devi creare un nuovo preventivo da zero (oppure duplicare
              quello sbagliato, ma il flusso si porterà dietro identico).
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Questa guida ti aiuta a capire quale dei due scegliere senza rischiare di
              fare l'errore più comune, cioè confondere il <strong>tipo di
              preventivo</strong> (semplice / configurato di commessa) con il
              <strong> flusso commerciale</strong>. Sono due cose diverse, anche se a volte
              si intrecciano. Ne parliamo nella sezione "Come scegliere alla creazione".
            </p>
          </section>
          <section id="flusso-vendita" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Il flusso Vendita</h2>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Il flusso <BadgeFlussoCommerciale flusso="VENDITA" /> è il flusso
              "commerciale puro". Lo usi quando stai vendendo qualcosa che è già pronto,
              o che diventerà pronto in tempi rapidi e prevedibili, e che il cliente paga
              e si porta via (o gli arriva a casa con un corriere). Niente cantieri,
              niente lavorazioni in commessa, niente SAL, niente capitolato firmato.
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">Casi tipici di vendita:</p>
            <ul>
              <li>Vendita di articoli a magazzino (es. piastrelle stock, accessori, articoli pronti).</li>
              <li>Vendita di servizi (es. ore di consulenza, manutenzioni straordinarie spot).</li>
              <li>Vendita di articoli preconfigurati a prezzo di listino (es. bagno-in-scatola con composizione fissa, articolo che esiste in più varianti a catalogo).</li>
              <li>Vendita "a corpo" di un bundle commerciale composto da articoli reali a un prezzo unico.</li>
            </ul>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Cosa <strong>non</strong> puoi fare nel flusso Vendita:
            </p>
            <ul>
              <li>Non puoi inserire righe di tipo <em>articolo configurato di commessa</em> (la regola è bloccata anche tecnicamente: se provi, BOP risponde "riga non ammessa nel flusso VENDITA").</li>
              <li>Non puoi gestire SAL o avanzamento lavori.</li>
              <li>Non puoi firmare un capitolato come parte dello stesso preventivo.</li>
            </ul>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              A valle, dopo la conversione in ordine, il flusso Vendita produce:
              <strong> DDT di vendita</strong> (causale: vendita) e
              <strong> fattura immediata</strong> a chiusura della consegna. Non ci sono
              DDT di spostamento, non ci sono fatture di acconto SAL, non c'è un
              contratto di appalto a monte.
            </p>
          </section>
          <section id="flusso-appalto" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Il flusso Appalto/Contratto</h2>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Il flusso <BadgeFlussoCommerciale flusso="APPALTO_CONTRATTO" /> è il flusso
              "di commessa". Lo usi quando il preventivo non è una vendita immediata di
              qualcosa di pronto, ma l'apertura di un lavoro: c'è una lavorazione, un
              cantiere o un servizio articolato che richiede tempo, materiali, gestione
              di avanzamenti. C'è quasi sempre un capitolato firmato dal cliente che
              descrive cosa stiamo facendo e a quali condizioni.
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">Casi tipici di appalto/contratto:</p>
            <ul>
              <li>Realizzazione di un bagno completo "chiavi in mano" (configurazione costruita al momento dell'ordine, basata su un capitolato già firmato).</li>
              <li>Forniture e posa di un appartamento o di una villa.</li>
              <li>Contratti di assistenza ricorrente o di manutenzione programmata.</li>
              <li>Pacchetti di servizi ripetuti nel tempo (es. ispezioni annuali con SAL trimestrali).</li>
            </ul>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              In appalto/contratto puoi inserire <strong>tutte</strong> le tipologie di
              riga, comprese quelle che in vendita sono vietate: l'articolo configurato
              di commessa, la riga "differita in ordine" (configurazione costruita al
              momento dell'ordine), la riga ricorrente per servizi periodici.
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              A valle, dopo la conversione in ordine, l'appalto/contratto produce
              <strong> due flussi paralleli</strong>:
            </p>
            <ul>
              <li>Per i <strong>materiali fisici</strong>: DDT di spostamento (causale: trasporto a destinazione) verso il cantiere o il cliente, e poi DDT di reso interno quando serve. Le fatture seguono l'andamento del SAL, non la singola consegna.</li>
              <li>Per i <strong>servizi e le lavorazioni</strong>: SAL (stati avanzamento lavori) con fatture di acconto progressive, regolate dal capitolato firmato.</li>
            </ul>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Per questo motivo il flusso appalto/contratto, anche se sembra "più
              complicato" del flusso vendita, è quello giusto quasi sempre quando c'è di
              mezzo un cantiere, una posa, una lavorazione che richiede gestione di
              distinte, o un cliente con cui abbiamo un rapporto contrattuale strutturato.
            </p>
          </section>
          <section id="come-scegliere" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Come scegliere alla creazione</h2>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Quando apri il wizard di creazione preventivo, la prima cosa che vedi sono
              due card: <BadgeFlussoCommerciale flusso="VENDITA" /> e
              {' '}<BadgeFlussoCommerciale flusso="APPALTO_CONTRATTO" />. È la domanda numero
              uno. Solo dopo che hai risposto a questa, BOP ti chiede il <em>tipo di
              preventivo</em> (semplice o configurato di commessa), e solo in alcuni casi.
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              La regola pratica che funziona quasi sempre: chiediti{' '}
              <strong>"il cliente sta comprando qualcosa o sta facendo fare un lavoro?"</strong>.
            </p>
            <ul>
              <li>Sta comprando → <BadgeFlussoCommerciale flusso="VENDITA" /> (poi il tipo viene forzato automaticamente a "semplice", senza chiederti niente).</li>
              <li>Sta facendo fare un lavoro → <BadgeFlussoCommerciale flusso="APPALTO_CONTRATTO" /> (poi BOP ti chiede se è un preventivo "semplice" o "configurato di commessa").</li>
            </ul>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              La differenza tra preventivo "semplice" e "configurato di commessa" è
              un'altra cosa ancora, e riguarda il tema del capitolato e della
              duplicazione del cliente, non il flusso. È spiegata nei documenti
              progettuali e in altre guide; per gli scopi di questa pagina ti basta
              ricordare che, se hai scelto Vendita, il tipo viene fissato per te.
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Una cosa importante che vale la pena sottolineare: <strong>se non sei
              sicuro, è meglio sbagliare verso l'Appalto/Contratto che verso la
              Vendita</strong>. L'appalto è il flusso "più ricco", quello che ammette
              tutte le tipologie di riga. La vendita è il flusso "magro": se la scegli
              per sbaglio, ti accorgi subito che BOP non ti lascia inserire la riga di
              configurato di commessa, e devi rifare il preventivo. Viceversa, se scegli
              appalto per qualcosa che era una vendita normale, il preventivo funziona
              comunque (perché tutte le righe sono ammesse), ma a valle si comporta come
              una commessa, e tu finirai per emettere SAL invece di fatture immediate,
              cosa che non ha senso per una vendita di articoli a magazzino. Insomma:
              l'errore è recuperabile in tutti e due i casi, ma sbagliare verso vendita
              si nota subito, sbagliare verso appalto si nota dopo, ed è più fastidioso.
            </p>
          </section>
          <section id="cosa-cambia-valle" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Cosa cambia a valle</h2>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              La scelta del flusso in testata al preventivo si propaga automaticamente
              all'ordine cliente quando converti il preventivo. L'ordine eredita il
              flusso esattamente uguale, non si può cambiare nemmeno in conversione.
              Lo stesso vale se duplichi un preventivo per crearne uno simile: la copia
              nasce con lo stesso flusso dell'originale.
            </p>
            <p className="bop-guida-ciclo-attivo__paragrafo">Quello che cambia, in concreto, a seconda del flusso:</p>
            <table>
              <thead>
                <tr>
                  <th>Aspetto</th>
                  <th>Vendita</th>
                  <th>Appalto / Contratto</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Tipologie di riga ammesse</td>
                  <td>Articolo semplice, articolo preconfigurato, voce libera, "a corpo"</td>
                  <td>Tutte le tipologie, comprese: configurato di commessa, differita in ordine, ricorrente</td>
                </tr>
                <tr>
                  <td>Tipo preventivo "semplice"</td>
                  <td>Forzato a semplice (non te lo chiede)</td>
                  <td>Ammesso</td>
                </tr>
                <tr>
                  <td>Tipo preventivo "configurato di commessa"</td>
                  <td>Vietato</td>
                  <td>Ammesso</td>
                </tr>
                <tr>
                  <td>Capitolato firmato</td>
                  <td>Non previsto</td>
                  <td>Previsto e collegabile al preventivo/ordine</td>
                </tr>
                <tr>
                  <td>DDT</td>
                  <td>DDT di vendita</td>
                  <td>DDT di spostamento + eventuali resi</td>
                </tr>
                <tr>
                  <td>Fatturazione</td>
                  <td>Fattura immediata</td>
                  <td>Fatture di acconto SAL</td>
                </tr>
                <tr>
                  <td>Distinte di lavorazione</td>
                  <td>Non previste</td>
                  <td>Previste, articolate per SAL</td>
                </tr>
              </tbody>
            </table>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Questa tabella è la sintesi più operativa di cosa significa la scelta.
              Tienila a portata di mano (o torna a questa pagina) le prime volte.
            </p>
          </section>
          <section id="casi-pratici" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Casi pratici</h2>
            <p className="bop-guida-ciclo-attivo__paragrafo">Quattro casi presi da scenari reali del gruppo:</p>

            <h3>1. Cliente che compra piastrelle a stock</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Un cliente chiama, dice: "Vorrei 80 metri quadri di queste piastrelle, le
              vedo a magazzino, le pago e me le porto via". Flusso:{' '}
              <BadgeFlussoCommerciale flusso="VENDITA" />. Tipo preventivo: forzato a
              semplice. A valle: DDT di vendita, fattura immediata. Nessun capitolato,
              nessun SAL.
            </p>

            <h3>2. Bagno chiavi-in-mano in una villa</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Un cliente firma un capitolato per realizzare due bagni completi nella sua
              villa, posa inclusa. La configurazione esatta degli articoli (sanitari,
              rubinetterie, pavimenti) verrà definita al momento dell'ordine, dopo
              sopralluogo. Flusso:{' '}
              <BadgeFlussoCommerciale flusso="APPALTO_CONTRATTO" />. Tipo preventivo:
              configurato di commessa (perché ha un capitolato, e la configurazione
              "differita in ordine" non sarebbe ammessa in vendita).
            </p>

            <h3>3. Manutenzione programmata annuale</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Un cliente sottoscrive un contratto annuale di assistenza con quattro
              interventi trimestrali. Flusso:{' '}
              <BadgeFlussoCommerciale flusso="APPALTO_CONTRATTO" /> (è un contratto, non
              una vendita). Tipo preventivo: semplice (non c'è bisogno di configurazione
              o duplicazione cliente). Le righe ricorrenti sono ammesse, le fatture
              seguono il piano contrattuale.
            </p>

            <h3>4. Vendita di un articolo preconfigurato di catalogo</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Un cliente compra un "kit bagno preconfigurato modello Aurora" che è già
              in catalogo come articolo preconfigurato. Lo paga e gli arriva a casa con
              un corriere. Flusso: <BadgeFlussoCommerciale flusso="VENDITA" />. Tipo
              preventivo: forzato a semplice. La riga "articolo preconfigurato" è
              ammessa in vendita. DDT di vendita e fattura immediata.
            </p>
          </section>
          <section id="errori-comuni" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Errori comuni</h2>

            <h3>Confondere "tipo preventivo" e "flusso commerciale"</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Sono due cose diverse, anche se a volte sembrano sovrapporsi. Il flusso
              commerciale è la prima domanda del wizard e risponde alla domanda
              "vendita o lavoro?". Il tipo preventivo è una sotto-domanda, e
              risponde a "c'è di mezzo un capitolato firmato con un cliente che potrebbe
              essere diverso da chi pagherà fattura?".
            </p>

            <h3>Scegliere Vendita perché "sembra più semplice"</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Il flusso vendita è più semplice, ma è anche più restrittivo. Se nel
              preventivo dovrai inserire una configurazione costruita su misura, o una
              riga ricorrente, o una riga "differita in ordine", BOP ti bloccherà
              perché quel tipo di riga non è ammesso in vendita. Dovrai cancellare il
              preventivo e rifarlo come appalto.
            </p>

            <h3>Pensare di poter "cambiare flusso" dopo</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Non si può. Il flusso è immutabile dalla creazione. Se sbagli, le
              soluzioni sono due: cancelli e rifai, oppure duplichi e correggi (ma la
              duplicazione mantiene il flusso originale). Se il preventivo è già stato
              convertito in ordine, l'ordine eredita lo stesso flusso e non si cambia
              nemmeno lì.
            </p>

            <h3>Mettere righe "esotiche" in un preventivo Vendita</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Se provi a inserire in un preventivo vendita una riga di tipo configurato
              di commessa, BOP risponde con un errore esplicito ("Riga di tipo
              CONFIGURATO_COMMESSA non ammessa nel flusso VENDITA"). Non è un bug:
              è una protezione progettata.
            </p>

            <h3>Convertire un preventivo Vendita in un ordine "di cantiere"</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              La conversione è automatica e propaga il flusso. Se hai creato il
              preventivo come vendita, l'ordine sarà vendita. Non puoi forzare la
              conversione in un ordine appalto. È coerente: il flusso è una proprietà
              del lavoro commerciale, non un attributo cambiabile a piacimento.
            </p>
          </section>
          <section id="faq" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">FAQ</h2>

            <h3>Cosa succede ai preventivi vecchi, fatti prima di T8?</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Tutti i preventivi e gli ordini storici, esistenti prima dell'introduzione
              del campo flusso commerciale, sono stati impostati automaticamente come{' '}
              <BadgeFlussoCommerciale flusso="APPALTO_CONTRATTO" />. È la scelta giusta
              perché ante-T8 di fatto tutto il flusso operativo BOP era pensato come
              appalto. Se hai un preventivo storico che era in realtà una vendita,
              puoi solo decidere di non riconvertirlo / non duplicarlo da lì.
            </p>

            <h3>Posso filtrare gli elenchi per flusso?</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Sì. Negli elenchi preventivi e ordini c'è una colonna "Flusso" che mostra
              il badge, e nella toolbar dei filtri c'è un selettore "Tutti / Vendita /
              Appalto-Contratto". È un modo rapido per vedere quanti preventivi vendita
              e quanti appalto stai gestendo in un dato momento.
            </p>

            <h3>Si vede sempre il flusso quando apro un preventivo?</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Sì. Il badge del flusso commerciale è in testata della scheda preventivo
              accanto al chip dello stato, ed è in testata anche della scheda ordine
              dopo la conversione. È sempre visibile in alto.
            </p>

            <h3>Cosa succede se duplico un preventivo?</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              La duplicazione mantiene identico il flusso commerciale dell'originale. Il
              flusso è immutabile non solo all'interno di una stessa entità, ma anche
              attraverso le duplicazioni. Se vuoi un preventivo identico ma con flusso
              diverso, devi crearne uno nuovo dal wizard.
            </p>

            <h3>Esiste un modo per "convertire" un Vendita in Appalto?</h3>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              No. Anche se a livello tecnico potrebbe sembrare possibile, è una
              decisione progettuale precisa: il flusso descrive la natura economica e
              contrattuale del lavoro, e cambiarlo significa snaturarlo. Se ti accorgi
              di aver sbagliato, la soluzione corretta è creare un nuovo preventivo e,
              se quello sbagliato non è ancora andato avanti, cancellarlo.
            </p>
          </section>
          <section id="glossario" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Glossario</h2>
            <dl>
              <dt>Flusso commerciale</dt>
              <dd>
                La proprietà di testata del preventivo (e poi dell'ordine) che descrive
                se si tratta di una vendita o di un appalto/contratto. Due valori
                possibili: <code>VENDITA</code> e <code>APPALTO_CONTRATTO</code>. È
                immutabile dopo la creazione.
              </dd>

              <dt>Tipo preventivo</dt>
              <dd>
                Un'altra proprietà del preventivo, indipendente dal flusso. Valori:{' '}
                <code>SEMPLICE</code> o <code>CONFIGURATO_COMMESSA</code>. Il
                configurato di commessa riguarda principalmente la gestione di
                capitolato e l'eventuale doppia anagrafica cliente (chi firma
                vs chi paga).
              </dd>

              <dt>Capitolato</dt>
              <dd>
                Documento firmato dal cliente che descrive l'oggetto del lavoro, le
                condizioni e gli importi. Tipico del flusso Appalto/Contratto.
              </dd>

              <dt>SAL</dt>
              <dd>
                Stato avanzamento lavori. È il meccanismo tipico dell'appalto: invece
                di fatturare a fine consegna, si fattura periodicamente in base
                all'avanzamento del lavoro.
              </dd>

              <dt>DDT di vendita / DDT di spostamento</dt>
              <dd>
                Documenti di trasporto con causali diverse. La vendita usa "DDT di
                vendita" (causale: vendita). L'appalto usa "DDT di spostamento"
                (causale: trasporto a destinazione), perché il materiale che
                consegniamo al cantiere non è ancora "venduto" in senso fiscale, lo
                sarà solo a SAL emesso.
              </dd>

              <dt>Riga ricorrente</dt>
              <dd>
                Tipo di riga che descrive un servizio ripetuto nel tempo (es.
                manutenzione mensile). Ammessa solo in Appalto/Contratto.
              </dd>

              <dt>Riga differita in ordine</dt>
              <dd>
                Riga la cui configurazione esatta verrà definita al momento della
                creazione dell'ordine, non al preventivo. Esempio classico: "bagno
                chiavi-in-mano" in cui i materiali vengono scelti dopo il sopralluogo.
                Ammessa solo in Appalto/Contratto.
              </dd>

              <dt>Articolo "a corpo"</dt>
              <dd>
                Bundle commerciale di articoli reali aggregati a un prezzo unico.
                Ammesso in entrambi i flussi.
              </dd>
            </dl>
          </section>
          <section id="riferimenti" className="bop-guida-ciclo-attivo__sezione">
            <h2 className="bop-guida-ciclo-attivo__sezione-titolo">Riferimenti</h2>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Per chi vuole approfondire la storia progettuale del flusso commerciale e
              le decisioni che lo hanno definito, i documenti interni di disegno sono:
            </p>
            <ul>
              <li><code>ordini-05-ponte-preventivo-ordine.md</code> — Parte II: il documento canonico del lotto L2-pre / T8.</li>
              <li><code>ordini-00-mappa-strategica-v4.md</code> — quadro generale del Nodo 5 e della sua organizzazione in lotti.</li>
              <li><code>ordini-01-tipologie-articolo-preventivi.md</code> — tipologie di riga ammesse, compatibilità con i flussi.</li>
              <li><code>ordini-02-modello-ordine-ricco.md</code> — struttura dell'ordine cliente e ereditarietà del flusso.</li>
              <li><code>ordini-03-conversione-preventivo-ordine.md</code> — meccanica della conversione e propagazione del flusso all'ordine.</li>
              <li><code>preventivi-disegno-dati-v3.md</code> — dominio del tipo preventivo (SEMPLICE / CONFIGURATO_COMMESSA).</li>
            </ul>
            <p className="bop-guida-ciclo-attivo__paragrafo">
              Per dubbi operativi su un caso specifico, parlane con Paolo o apri un
              ticket via i canali interni del gruppo.
            </p>
          </section>
        </main>

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
